import { useCallback, useEffect, useRef, useState } from 'react'

import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  PublicKeyCredentialRequestOptionsJSON,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser'
import { usePathname, useSearchParams } from 'next/navigation'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { DividerWithLabel } from '~/components/DividerWithLabel'
import { SplitsLink as Link } from '~/components/Link'
import { Button, TextInput } from '~/components/component-library'
import { SPLITS_LOGIN_PENDING_KEY } from '~/constants/localStorage'
import { useAuth } from '~/context/AuthProvider'
import { useIsStandalone } from '~/hooks/useIsStandalone'
import { useServerRequests } from '~/hooks/useRequests'
import { useCookieStore } from '~/store/cookieStore'
import { isValidEmail } from '~/utils/authUser'
import { captureException } from '~/utils/errors'
import { startPasskeyAuthentication } from '~/utils/passkeys'
import { clearBatcherCache } from '~/utils/requests'

type LoginFormValues = {
  email: string
}

class PasskeyError extends Error {
  originalError: unknown

  constructor(message: string, originalError: unknown) {
    super(message)
    this.originalError = originalError
  }
}

const isSplitsEmail = (emailAddress: string) => {
  return emailAddress.split('@')[1] === 'splits.org'
}

export const LoginForm: React.FC = () => {
  const [isLoadingPasskey, setIsLoadingPasskey] = useState(false)
  const [isLoadingEmail, setIsLoadingEmail] = useState(false)
  const autofillInitiatedRef = useRef(false)

  const { setLoginModalState } = useAuth()
  const { serverPostRequest, serverGetRequest } = useServerRequests()
  const { control, handleSubmit } = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
  })

  const email = useWatch({ control, name: 'email' })

  const [loginRequestMessage, setLoginRequestMessage] = useState('')

  const activeOrgId = useCookieStore((s) => s.activeOrgId)
  const setAuthToken = useCookieStore((s) => s.setAuthToken)
  const setActiveOrgId = useCookieStore((s) => s.setActiveOrgId)

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isStandalone = useIsStandalone()

  const performDiscoverablePasskeyLogin = async (
    { useBrowserAutofill }: { useBrowserAutofill: boolean } = {
      useBrowserAutofill: false,
    },
  ) => {
    const optionsResponse = await serverGetRequest<{
      options: PublicKeyCredentialRequestOptionsJSON
    }>({
      path: '/api/auth/passkey-options-discoverable',
      params: {},
      onError: (e) => {
        if (e.response?.status === 429) {
          throw new PasskeyError(
            'Too many attempts. Please try again later.',
            e,
          )
        } else if (e.code === 'ERR_NETWORK') {
          throw new PasskeyError(
            'Unexpected server or network error. Please try again later.',
            e,
          )
        } else {
          throw new PasskeyError(
            'Failed to get passkey authentication options. Please try again later.',
            e,
          )
        }
      },
    })

    if (!optionsResponse) return

    const newAuthToken = optionsResponse.headers.authorization

    let authResp
    try {
      authResp = await startAuthentication({
        optionsJSON: optionsResponse.data.options,
        useBrowserAutofill,
      })
    } catch (error) {
      // Ceremony was aborted by another starting (e.g., cancelCeremony) — not an error
      if (error instanceof Error && error.name === 'AbortError') return
      if (error instanceof Error && error.name === 'NotAllowedError') {
        throw new PasskeyError(
          'Passkey authentication was cancelled or not allowed.',
          error,
        )
      }
      throw new PasskeyError('Authentication failed. Please try again.', error)
    }

    const loginResult = await serverPostRequest<{
      activeOrgId: string
    }>({
      path: `/api/auth/passkey-verify-discoverable`,
      params: {
        authentication: JSON.stringify(authResp),
        activeOrgId,
        authToken: newAuthToken,
      },
      errorToastId: 'login-passkey',
      errorMessage: 'Failed to log in',
    })

    if (!loginResult || loginResult.status !== 200) return

    clearBatcherCache()
    setAuthToken(newAuthToken)
    if (
      loginResult.data.activeOrgId &&
      loginResult.data.activeOrgId !== activeOrgId
    ) {
      setActiveOrgId(loginResult.data.activeOrgId)
    }
    setLoginModalState(false)
  }

  const onSubmit: SubmitHandler<LoginFormValues> = useCallback(
    async ({ email }) => {
      setLoginRequestMessage('')
      setIsLoadingEmail(true)
      // Cancel any pending conditional mediation to avoid concurrent WebAuthn ceremonies
      WebAuthnAbortService.cancelCeremony()
      let sendEmail = false

      const options = isSplitsEmail(email)
        ? await serverGetRequest<{
            options: PublicKeyCredentialRequestOptionsJSON
          }>({
            path: '/api/auth/passkey-options',
            params: {
              email,
            },
          })
        : undefined

      if (options && options.data && options.data.options) {
        const attResp = await startPasskeyAuthentication(options.data.options, {
          toastId: 'login-passkey',
          onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
            sendEmail = true
            // Unsupported browser — fall back to email silently
            if (isUnsupportedError) return
            // User likely cancelled the registration
            if (!isNotAllowedError) {
              captureException(error, {
                tags: {
                  feature: 'passkey',
                  operation: 'login',
                  errorType: error instanceof Error ? error.name : 'unknown',
                },
                extra: {
                  errorMessage:
                    error instanceof Error ? error.message : String(error),
                  email,
                  isWebAuthnError:
                    error instanceof Error && error.name?.includes('Error'),
                },
              })
            }
          },
        })

        if (attResp) {
          const verificationResponse = await serverPostRequest<{
            activeOrgId: string
          }>({
            path: `/api/auth/passkey-verify`,
            params: {
              authentication: JSON.stringify(attResp),
              email,
              activeOrgId,
            },
            successMessageOptions: {
              id: 'login-passkey',
            },
            errorToastId: 'login-passkey',
            successMessage: 'Successfully logged in',
            errorMessage: 'Failed to log in',
          })

          if (verificationResponse) {
            const newAuthToken = verificationResponse.headers.authorization

            if (newAuthToken) {
              clearBatcherCache()
              setAuthToken(newAuthToken)
              setLoginModalState(false)
              if (
                verificationResponse.data.activeOrgId &&
                verificationResponse.data.activeOrgId !== activeOrgId
              ) {
                setActiveOrgId(verificationResponse.data.activeOrgId)
              }
            }
          }
        } else {
          sendEmail = true
        }
      } else {
        sendEmail = true
      }

      toast.dismiss('login-passkey')

      if (sendEmail) {
        const newParams = new URLSearchParams(searchParams.toString())

        newParams.delete('referral')
        const queryString = newParams.toString().trim()
        const nextPath =
          queryString !== '' ? `${pathname}?${queryString}` : pathname

        const referralCode = searchParams.get('referral')

        const result = await serverPostRequest({
          path: '/api/auth/send_link',
          params: { email, nextPath, referralCode },
          errorMessage: 'Failed to send login link',
        })
        if (result) {
          setLoginRequestMessage('Link sent. Check your email to login.')

          // Set a localStorage item to indicate that a login is pending.
          // Used to auto-continue on the client after redirecting back from email.
          try {
            localStorage.setItem(
              SPLITS_LOGIN_PENDING_KEY,
              Date.now().toString(),
            )
          } catch {
            // localStorage may be unavailable (Safari private browsing, etc.)
          }
        }
      }

      setIsLoadingEmail(false)
    },
    [
      serverGetRequest,
      activeOrgId,
      serverPostRequest,
      setAuthToken,
      setActiveOrgId,
      setLoginModalState,
      searchParams,
      pathname,
    ],
  )

  const [webAuthnSupported, setWebAuthnSupported] = useState(
    // Start optimistic so the button doesn't flash on supported browsers
    browserSupportsWebAuthn(),
  )
  useEffect(() => {
    if (!browserSupportsWebAuthn()) {
      setWebAuthnSupported(false)
      return
    }
    platformAuthenticatorIsAvailable().then((available) => {
      setWebAuthnSupported(available)
    })
  }, [])

  if (isStandalone && !webAuthnSupported) {
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col space-y-4">
          <TextInput
            label="Email address"
            control={control}
            inputName={'email'}
            placeholder={'name@example.com'}
            footer={
              loginRequestMessage ? (
                <p className="text-green-500">{loginRequestMessage}</p>
              ) : (
                ''
              )
            }
            inputType="email"
            autoComplete={'email'}
          />
          <Button
            variant="primary"
            fullWidth={true}
            isDisabled={isLoadingEmail || !isValidEmail(email)}
            isLoading={isLoadingEmail}
            type="submit"
          >
            Continue with email
          </Button>
          <p className="text-sm text-secondary">
            By continuing, you agree to our{' '}
            <Link
              path="https://splits.org/terms/"
              target="tab"
              className="underline underline-offset-2"
            >
              Terms of service
            </Link>
            .
          </p>
        </div>
      </form>
    )
  }

  if (isStandalone) {
    return (
      <div className="flex flex-col space-y-4">
        <Button
          type="button"
          fullWidth={true}
          variant="primary"
          isLoading={isLoadingPasskey}
          isDisabled={isLoadingPasskey}
          onClick={async () => {
            WebAuthnAbortService.cancelCeremony()
            try {
              setIsLoadingPasskey(true)
              await performDiscoverablePasskeyLogin()
            } catch (error) {
              toast.error('Passkey Login Failed', {
                description:
                  error instanceof PasskeyError
                    ? error.message
                    : 'An unknown error occurred',
              })
            } finally {
              setIsLoadingPasskey(false)
            }
          }}
        >
          Sign in with passkey
        </Button>
        <p className="text-sm text-secondary">
          By continuing, you agree to our{' '}
          <Link
            path="https://splits.org/terms/"
            target="tab"
            className="underline underline-offset-2"
          >
            Terms of service
          </Link>
          .
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={'relative flex flex-col space-y-4'}>
        <TextInput
          label="Email address"
          control={control}
          inputName={'email'}
          onFocus={async () => {
            if (autofillInitiatedRef.current) return
            const autofillAvailable = await browserSupportsWebAuthnAutofill()
            if (!autofillAvailable) return

            autofillInitiatedRef.current = true
            try {
              await performDiscoverablePasskeyLogin({
                useBrowserAutofill: true,
              })
            } catch {
              // Conditional mediation was aborted by another ceremony — expected
            }
          }}
          placeholder={'name@example.com'}
          footer={
            loginRequestMessage ? (
              <p className="text-green-500">{loginRequestMessage}</p>
            ) : (
              ''
            )
          }
          inputType="email"
          autoComplete={'email webauthn'}
        />

        <div>
          <Button
            variant="primary"
            fullWidth={true}
            isDisabled={isLoadingEmail || !isValidEmail(email)}
            isLoading={isLoadingEmail}
            type="submit"
          >
            Continue with email
          </Button>

          {webAuthnSupported && (
            <>
              <div className="my-4">
                <DividerWithLabel label="or" />
              </div>

              <Button
                type="button"
                fullWidth={true}
                variant="secondary"
                isLoading={isLoadingPasskey}
                isDisabled={isLoadingPasskey}
                onClick={async () => {
                  // Cancel any pending conditional mediation to avoid concurrent WebAuthn ceremonies
                  WebAuthnAbortService.cancelCeremony()
                  try {
                    setIsLoadingPasskey(true)
                    await performDiscoverablePasskeyLogin()
                  } catch (error) {
                    toast.error('Passkey Login Failed', {
                      description:
                        error instanceof PasskeyError
                          ? error.message
                          : 'An unknown error occurred',
                    })
                  } finally {
                    setIsLoadingPasskey(false)
                  }
                }}
              >
                Continue with passkey
              </Button>
            </>
          )}

          <>
            <p className="mt-4 text-sm text-secondary">
              By continuing, you agree to our{' '}
              <Link
                path="https://splits.org/terms/"
                target="tab"
                className="underline underline-offset-2"
              >
                Terms of service
              </Link>
              .
            </p>
          </>
        </div>
      </div>
    </form>
  )
}
